import React, { useEffect, useState, useRef } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/Card";
import { Input, Label, Select, Textarea } from "../components/ui/Input";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { Upload, Image as ImageIcon, AlertTriangle, RotateCw, ShieldCheck, KeyRound, Shield, Printer, ChevronDown, ChevronUp, Eye, EyeOff } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "../components/ui/Toast";
import { parseApiError } from "../lib/utils";
import { PrintPreview } from "../components/ui/PrintPreview";
import { BillThermal, PhieuXuatKho, InvoiceA4, InvoiceA5Landscape } from "../components/print/BillTemplates";

const MAX_LOGO_BYTES = 300 * 1024;

// Dữ liệu mẫu để xem trước bill
const DEMO_ORDER = {
  order_id: "demo",
  order_code: "DH-0001",
  customer_name: "Nguyễn Văn A",
  customer_phone: "0901234567",
  customer_address: "123 Đường Lê Lợi, Q.1, TP.HCM",
  created_at: new Date().toISOString(),
  payment_method: "cash",
  is_paid: true,
  items: [
    { name: "Bánh Bao Thịt", quantity: 10, price: 15000, subtotal: 150000 },
    { name: "Bánh Bao Nhân Ngọt", quantity: 5, price: 12000, subtotal: 60000 },
    { name: "Bánh Bao Chay", quantity: 8, price: 10000, subtotal: 80000 },
  ],
  subtotal: 290000,
  discount: 10000,
  discount_percent: 0,
  discount_amount: 10000,
  shipping_fee: 15000,
  total: 295000,
  note: "Giao trước 10h sáng",
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

const DEFAULT = {
  shop_name: "Tiệm Bánh Bao",
  address: "",
  phone: "",
  email: "",
  website: "",
  tax_id: "",
  bank_name: "",
  bank_account: "",
  bank_account_holder: "",
  logo_url: "",
  bill_show_logo: true,
  bill_show_address: true,
  bill_show_phone: true,
  bill_show_email: false,
  bill_show_website: false,
  bill_show_tax_id: false,
  bill_show_bank_qr: true,
  bill_show_discount: true,
  bill_show_subtotal: true,
  bill_show_after_discount: true,
  bill_show_shipping: true,
  bill_footer_text: "Cảm ơn quý khách. Hẹn gặp lại!",
  bill_invoice_title: "HÓA ĐƠN BÁN HÀNG",
  default_language: "vi",
  bill_accent_color: "#2D4A22",
  bill_logo_position: "left",
  bill_signature_customer: false,
  bill_signature_shipper: false,
  bill_signature_warehouse: false,
  bill_signature_accountant: false,
  bill_fixed_notes: ["", "", "", "", ""],
  bill_print_copies: 1,
  bill_copy2_label: "LIÊN KHÁCH",
  pxk_title: "Phiếu Xuất Kho Bán Hàng",
  pxk_creator: "",
  pxk_warehouse_keeper: "",
  pxk_default_unit: "Cái",
  pxk_sig_buyer: true,
  pxk_sig_warehouse: true,
  pxk_sig_shipper: true,
  pxk_sig_creator: true,
  pxk_show_seller: true,
  pxk_show_sku: true,
  pxk_paper_size: "a4",
};

export default function Settings() {
  const { t, lang, toggleLang } = useI18n();
  const { user } = useAuth();
  const [form, setForm] = useState(DEFAULT);
  const [serverTime, setServerTime] = useState(null);
  const [now, setNow] = useState(new Date());
  const [secStatus, setSecStatus] = useState({ has_pin2: false, has_delete_pins: false });
  const [pin2Form, setPin2Form] = useState({ account_password: "", new_pin: "" });
  const [delPinsForm, setDelPinsForm] = useState({ account_password: "", pin_a: "", pin_b: "" });
  const [showPw, setShowPw] = useState({ pin2_acc: false, pin2_new: false, del_acc: false, del_a: false, del_b: false });
  const fileRef = useRef(null);
  const bgImgRef = useRef(null);
  const [billOpen, setBillOpen] = useState(false);
  const [billTab, setBillTab] = useState("content");
  const [billPreviewVariant, setBillPreviewVariant] = useState("pxk");
  const [companyOpen, setCompanyOpen] = useState(false);
  const [bankOpen, setBankOpen] = useState(false);
  const [secOpen, setSecOpen] = useState(false);
  const [backupOpen, setBackupOpen] = useState(false);

  // ── Nền trang ──
  const [bgOpen, setBgOpen] = useState(false);
  const [bgConfig, setBgConfig] = useState(() => {
    try { return JSON.parse(localStorage.getItem("app_bg") || "null") || { type: "default", value: "", opacity: 0.08 }; }
    catch { return { type: "default", value: "", opacity: 0.08 }; }
  });

  const applyBg = (cfg) => {
    setBgConfig(cfg);
    localStorage.setItem("app_bg", JSON.stringify(cfg));
    // Dispatch event để Layout.jsx lắng nghe
    window.dispatchEvent(new CustomEvent("app_bg_change", { detail: cfg }));
  };

  const BG_PRESETS = [
    { id: "default", label: "Mặc định", preview: "bg-paper" },
    { id: "gradient_green", label: "Xanh lá", value: "linear-gradient(135deg,#e8f5e9 0%,#f4f1ea 50%,#fff8e1 100%)" },
    { id: "gradient_blue", label: "Xanh dương", value: "linear-gradient(135deg,#e3f2fd 0%,#fafafa 50%,#f3e5f5 100%)" },
    { id: "gradient_warm", label: "Ấm áp", value: "linear-gradient(135deg,#fff8e1 0%,#fce4ec 50%,#fafafa 100%)" },
    { id: "gradient_dark", label: "Tối nhẹ", value: "linear-gradient(135deg,#e8eaf6 0%,#fafafa 50%,#e0f2f1 100%)" },
    { id: "pattern_dot", label: "Chấm bi", value: "dot" },
    { id: "pattern_grid", label: "Lưới ô", value: "grid" },
    { id: "pattern_wave", label: "Sóng lượn", value: "wave" },
  ];

  const refreshTime = async () => {
    try {
      const { data } = await api.get("/system/time");
      setServerTime(data);
    } catch {}
  };

  const refreshSecStatus = async () => {
    try {
      const { data } = await api.get("/security/status");
      setSecStatus(data);
    } catch {}
  };

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      const merged = { ...DEFAULT, ...data };
      const notes = (data.bill_fixed_notes || []).slice(0, 5);
      while (notes.length < 5) notes.push("");
      merged.bill_fixed_notes = notes;
      setForm(merged);
    });
    refreshTime();
    refreshSecStatus();
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const savePin2 = async () => {
    if (!pin2Form.account_password || !pin2Form.new_pin) {
      toast({ title: "Cần nhập mật khẩu tài khoản và mật khẩu cấp 2 mới", variant: "error" });
      return;
    }
    try {
      await api.post("/security/pin2", pin2Form);
      toast({ title: "Đã cập nhật mật khẩu cấp 2", variant: "success" });
      setPin2Form({ account_password: "", new_pin: "" });
      refreshSecStatus();
    } catch (err) {
      toast({ title: parseApiError(err, "Lỗi"), variant: "error" });
    }
  };

  const clearPin2 = async () => {
    const pwd = window.prompt("Nhập mật khẩu tài khoản để tắt mật khẩu cấp 2:");
    if (!pwd) return;
    try {
      await api.delete(`/security/pin2?account_password=${encodeURIComponent(pwd)}`);
      toast({ title: "Đã tắt mật khẩu cấp 2", variant: "success" });
      refreshSecStatus();
    } catch (err) {
      toast({ title: parseApiError(err, "Lỗi"), variant: "error" });
    }
  };

  const saveDeletePins = async () => {
    if (!delPinsForm.account_password || !delPinsForm.pin_a || !delPinsForm.pin_b) {
      toast({ title: "Cần đủ 3 trường", variant: "error" });
      return;
    }
    if (delPinsForm.pin_a === delPinsForm.pin_b) {
      toast({ title: "2 mật khẩu xóa phải khác nhau", variant: "error" });
      return;
    }
    try {
      await api.post("/security/delete-pins", delPinsForm);
      toast({ title: "Đã cập nhật 2 mật khẩu xóa", variant: "success" });
      setDelPinsForm({ account_password: "", pin_a: "", pin_b: "" });
      refreshSecStatus();
    } catch (err) {
      toast({ title: parseApiError(err, "Lỗi"), variant: "error" });
    }
  };

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      toast({ title: "Logo quá lớn", description: "Tối đa 300KB", variant: "error" });
      return;
    }
    try {
      const b64 = await fileToBase64(file);
      setForm((p) => ({ ...p, logo_url: b64 }));
      toast({ title: "Đã tải logo", variant: "success" });
    } catch {
      toast({ title: "Không đọc được file", variant: "error" });
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onSave = async (e) => {
    e?.preventDefault?.();
    try {
      await api.put("/settings", form);
      toast({ title: t("common.saved"), variant: "success" });
    } catch (err) {
      toast({ title: parseApiError(err) || t("common.error"), variant: "error" });
    }
  };

  const exportAllExcel = async () => {
    try {
      const r = await api.get("/system/export-all-excel", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `banhbao_export_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: parseApiError(err, "Lỗi xuất Excel"), variant: "error" });
    }
  };

  const backupZip = async () => {
    try {
      const r = await api.get("/system/backup-zip", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `banhbao_backup_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "")}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: parseApiError(err, "Lỗi tạo backup"), variant: "error" });
    }
  };

  const importAllRef = useRef(null);
  const onImportAll = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm(`Đồng bộ ngược dữ liệu từ "${file.name}"? Các dòng có cùng ID sẽ ghi đè, dòng mới sẽ thêm vào.`)) {
      if (importAllRef.current) importAllRef.current.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/system/import-all-excel", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const COLL_LABELS = {
        customers: "Khách hàng", products: "Sản phẩm",
        orders: "Đơn hàng", debt_payments: "Lịch sử thanh toán",
      };
      const imported = r.data.imported || {};
      const summary = Object.entries(imported)
        .filter(([k]) => k !== "orders_skipped_new")
        .map(([k, v]) => `${COLL_LABELS[k] || k}: ${v}`)
        .join(", ");
      let description = summary || "Không có dữ liệu";
      if (imported.orders_skipped_new) {
        description += `. Bỏ qua ${imported.orders_skipped_new} đơn hàng mới (không thể tạo đơn từ Excel vì thiếu chi tiết sản phẩm)`;
      }
      toast({ title: "Đã đồng bộ", description, variant: "success" });
    } catch (err) {
      toast({ title: parseApiError(err, "Lỗi đồng bộ"), variant: "error" });
    } finally {
      if (importAllRef.current) importAllRef.current.value = "";
    }
  };

  const onResetDemo = async () => {
    const text = prompt(`${t("settings.resetDemoConfirm")}\n\nGõ "YES_DELETE" để xác nhận:`);
    if (text !== "YES_DELETE") {
      toast({ title: "Đã hủy", variant: "info" });
      return;
    }
    try {
      const { data } = await api.post(`/system/reset-demo?confirm=YES_DELETE`);
      toast({ title: "Đã xóa", description: `Đơn: ${data.deleted.orders}, KH: ${data.deleted.customers}, SP: ${data.deleted.products}`, variant: "success" });
    } catch (err) {
      toast({ title: parseApiError(err) || t("common.error"), variant: "error" });
    }
  };

  const billChecks = [
    { key: "bill_show_logo", label: "Logo" },
    { key: "bill_show_address", label: t("common.address") },
    { key: "bill_show_phone", label: t("common.phone") },
    { key: "bill_show_email", label: t("common.email") },
    { key: "bill_show_website", label: t("settings.website") },
    { key: "bill_show_tax_id", label: t("settings.taxId") },
    { key: "bill_show_bank_qr", label: "QR Ngân hàng" },
    { key: "bill_show_discount", label: "Chiết khấu / Giảm giá" },
    { key: "bill_show_after_discount", label: "Sau chiết khấu" },
    { key: "bill_show_shipping", label: "Phí vận chuyển" },
  ];

  const vnNow = new Date(now.getTime() + (7 * 60 * 60 * 1000) + new Date().getTimezoneOffset() * 60 * 1000);

  return (
    <form onSubmit={onSave} className="space-y-6 animate-fade-in max-w-4xl" data-testid="settings-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t("settings.title")}</h1>
          <p className="text-sm text-ink-secondary mt-1">{t("tagline")}</p>
        </div>
        <Button type="submit" data-testid="settings-save">{t("common.save")}</Button>
      </div>

      {/* Server time card */}
      <Card data-testid="settings-server-time">
        <CardContent>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-xs uppercase tracking-wider text-ink-muted">{t("settings.serverTime")}</div>
              <div className="font-heading text-xl font-bold mt-0.5">
                {serverTime ? new Date(serverTime.utc).toLocaleString("vi-VN", { timeZone: "Asia/Ho_Chi_Minh" }) : "..."}
              </div>
              <div className="text-xs text-ink-muted mt-1">
                Trình duyệt: <span className="font-mono">{now.toLocaleString("vi-VN")}</span>
              </div>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={refreshTime} data-testid="settings-refresh-time">
              <RotateCw size={14} /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md border border-border bg-cream/50 overflow-hidden flex items-center justify-center shrink-0">
                {form.logo_url ? <img src={form.logo_url} alt="Logo" className="w-full h-full object-contain" /> : <ImageIcon size={18} className="text-ink-muted" />}
              </div>
              <div>
                <CardTitle>{form.shop_name || t("settings.company")}</CardTitle>
                <p className="text-sm text-ink-muted mt-0.5">{[form.phone, form.address].filter(Boolean).join(" · ") || "Bấm để chỉnh thông tin cơ sở"}</p>
              </div>
            </div>
            <Button type="button" variant="outline" onClick={() => setCompanyOpen(true)} className="flex items-center gap-2 shrink-0">
              <ImageIcon size={14} /> Chỉnh sửa
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* ── COMPANY MODAL ── */}
      {companyOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.45)" }} onClick={() => setCompanyOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden w-full max-w-lg" style={{ maxHeight: "90vh" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
              <div className="flex items-center gap-2 font-semibold text-base">
                <ImageIcon size={16} className="text-bamboo" /> {t("settings.company")}
              </div>
              <button type="button" onClick={() => setCompanyOpen(false)} className="text-ink-muted hover:text-ink text-xl leading-none px-1">✕</button>
            </div>
            <div className="overflow-y-auto flex-1 p-5 space-y-4">
              {/* Logo */}
              <div>
                <Label>{t("settings.logo")}</Label>
                <div className="flex items-start gap-4">
                  <div className="w-28 h-28 rounded-md border border-border bg-cream/50 overflow-hidden flex items-center justify-center shrink-0">
                    {form.logo_url ? <img src={form.logo_url} alt="Logo" className="w-full h-full object-contain" /> : <ImageIcon size={32} className="text-ink-muted" />}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input ref={fileRef} type="file" accept="image/*" onChange={onLogo} className="hidden" data-testid="settings-logo-file" />
                    <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="settings-logo-upload">
                      <Upload size={14} /> Tải logo
                    </Button>
                    {form.logo_url && (
                      <button type="button" onClick={() => setForm({ ...form, logo_url: "" })} className="text-xs text-red-600 hover:underline ml-2">Xóa logo</button>
                    )}
                    <div className="text-[11px] text-ink-muted">Tối đa 300KB. PNG/JPG/SVG. Recommended: vuông.</div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>{t("settings.companyName")} *</Label>
                  <Input value={form.shop_name} onChange={(e) => setForm({ ...form, shop_name: e.target.value })} required data-testid="settings-shop-name" />
                </div>
                <div>
                  <Label>{t("settings.taxId")}</Label>
                  <Input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} data-testid="settings-tax-id" />
                </div>
                <div className="sm:col-span-2">
                  <Label>{t("common.address")}</Label>
                  <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="settings-address" />
                </div>
                <div>
                  <Label>{t("common.phone")}</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="settings-phone" />
                </div>
                <div>
                  <Label>{t("common.email")}</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="settings-email" />
                </div>
                <div className="sm:col-span-2">
                  <Label>{t("settings.website")}</Label>
                  <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" data-testid="settings-website" />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-border bg-cream/30 shrink-0">
              <Button type="button" variant="outline" onClick={() => setCompanyOpen(false)}>Đóng</Button>
              <Button type="button" onClick={async () => { await onSave(); setCompanyOpen(false); }}>Lưu</Button>
            </div>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t("settings.bankInfo")}</CardTitle>
              <p className="text-sm text-ink-muted mt-1">
                {form.bank_name ? `${form.bank_name} · ${form.bank_account || "chưa có số TK"}` : "Thiết lập thông tin ngân hàng & QR thanh toán"}
              </p>
            </div>
            <Button type="button" variant="outline" onClick={() => setBankOpen(true)} className="flex items-center gap-2 shrink-0">
              🏦 Chỉnh sửa
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* ── BANK MODAL ── */}
      {bankOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.45)" }} onClick={() => setBankOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden w-full max-w-lg" style={{ maxHeight: "90vh" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
              <div className="flex items-center gap-2 font-semibold text-base">
                🏦 {t("settings.bankInfo")}
              </div>
              <button type="button" onClick={() => setBankOpen(false)} className="text-ink-muted hover:text-ink text-xl leading-none px-1">✕</button>
            </div>
            <div className="overflow-y-auto flex-1 p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>{t("settings.bankName")}</Label>
                  <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="VD: VietcomBank, Techcombank..." data-testid="settings-bank-name" />
                </div>
                <div>
                  <Label>{t("settings.bankAccount")}</Label>
                  <Input value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} placeholder="Số tài khoản" data-testid="settings-bank-account" />
                </div>
                <div className="sm:col-span-2">
                  <Label>{t("settings.bankAccountHolder")}</Label>
                  <Input value={form.bank_account_holder} onChange={(e) => setForm({ ...form, bank_account_holder: e.target.value })} placeholder="Tên chủ tài khoản (in hoa)" data-testid="settings-bank-holder" />
                </div>
              </div>
              <div className="text-xs text-ink-muted bg-cream/50 rounded-lg px-3 py-2">
                ℹ️ Khi đủ thông tin Ngân hàng + Số TK + Tên chủ TK, mã QR sẽ tự động xuất hiện trên bill in (nếu được bật).
              </div>
              {form.bank_name && form.bank_account && (
                <div className="flex items-center gap-4 p-3 bg-cream/40 rounded-xl border border-border">
                  {(() => {
                    const map = { "vietcombank": "VCB", "vcb": "VCB", "techcombank": "TCB", "tcb": "TCB", "bidv": "BIDV", "vietinbank": "ICB", "agribank": "VBA", "mbbank": "MB", "mb": "MB", "acb": "ACB", "tpbank": "TPB", "vpbank": "VPB", "sacombank": "STB", "hdbank": "HDB", "msb": "MSB", "shb": "SHB", "vib": "VIB", "lpbank": "LPB", "ocb": "OCB", "eximbank": "EIB" };
                    const code = map[(form.bank_name || "").toLowerCase().replace(/\s+/g, "")] || form.bank_name.replace(/\s+/g, "").toUpperCase();
                    return <img src={`https://img.vietqr.io/image/${code}-${form.bank_account}-compact2.png?amount=0${form.bank_account_holder ? `&accountName=${encodeURIComponent(form.bank_account_holder)}` : ""}`} alt="QR" crossOrigin="anonymous" className="rounded-lg border border-border" style={{ width: 100, height: 100, objectFit: "contain", background: "#fff" }} onError={(e) => { e.target.style.display = "none"; }} />;
                  })()}
                  <div className="text-sm space-y-0.5">
                    <div className="font-semibold">{form.bank_name}</div>
                    <div className="font-mono text-bamboo text-base tracking-wider">{form.bank_account}</div>
                    {form.bank_account_holder && <div className="text-xs text-ink-muted uppercase">{form.bank_account_holder}</div>}
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-border bg-cream/30 shrink-0">
              <Button type="button" variant="outline" onClick={() => setBankOpen(false)}>Đóng</Button>
              <Button type="button" onClick={async () => { await onSave(); setBankOpen(false); }}>Lưu</Button>
            </div>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Printer size={18} className="text-bamboo" />
              {t("settings.bill")}
            </CardTitle>
            <Button
              variant="outline"
              onClick={() => setBillOpen(true)}
              className="flex items-center gap-2"
            >
              <Printer size={14} /> Chỉnh sửa Bill
            </Button>
          </div>
          <p className="text-sm text-ink-muted mt-1">Tuỳ chỉnh logo, giao diện, ghi chú và xem trước khi in.</p>
        </CardHeader>
      </Card>

      {/* ── BILL MODAL ── */}
      {billOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.45)" }} onClick={() => setBillOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden w-full max-w-2xl" style={{ maxHeight: "90vh" }} onClick={(e) => e.stopPropagation()}>

            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
              <div className="flex items-center gap-2 font-semibold text-base">
                <Printer size={16} className="text-bamboo" /> Chỉnh sửa Bill
              </div>
              <button type="button" onClick={() => setBillOpen(false)} className="text-ink-muted hover:text-ink text-xl leading-none px-1">✕</button>
            </div>

            {/* Tab bar */}
            <div className="flex border-b border-border shrink-0">
              {[
                { key: "content", icon: "📋", label: "Nội dung" },
                { key: "qr",      icon: "🏦", label: "QR Ngân hàng" },
                { key: "preview", icon: "👁",  label: "Xem trước" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setBillTab(tab.key)}
                  className={`flex items-center gap-1.5 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                    billTab === tab.key
                      ? "border-bamboo text-bamboo"
                      : "border-transparent text-ink-muted hover:text-ink hover:border-border"
                  }`}
                >
                  <span>{tab.icon}</span> {tab.label}
                </button>
              ))}
            </div>

            {/* Tab content — scrollable */}
            <div className="overflow-y-auto flex-1">
            {/* ── TAB: NỘI DUNG ── */}
            {billTab === "content" && (
              <div className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">Chỉnh từng dòng trên Phiếu Xuất Kho</Label>
                  <span className="text-xs text-ink-muted bg-cream px-2 py-1 rounded-full">Bật/tắt · Sửa nội dung</span>
                </div>
                <div className="space-y-0 border border-border rounded-xl overflow-hidden">

                  {/* Dòng 1: Tên cơ sở */}
                  <div className="flex items-center gap-3 px-3 py-2.5 bg-white border-b border-border/60">
                    <span className="text-xs font-mono text-ink-muted w-5 select-none">1</span>
                    <input type="checkbox" checked={true} disabled className="opacity-40" title="Luôn hiển thị" />
                    <span className="text-xs font-semibold text-ink-muted w-32 shrink-0">Tên cơ sở</span>
                    <input
                      className="flex-1 border border-border/60 rounded-lg px-3 py-1.5 text-sm focus:border-bamboo focus:outline-none"
                      value={form.shop_name || ""}
                      onChange={(e) => setForm({ ...form, shop_name: e.target.value })}
                      placeholder="Tên cơ sở / cửa hàng..."
                    />
                  </div>

                  {/* Dòng 2: Mã số thuế */}
                  <div className="flex items-center gap-3 px-3 py-2.5 bg-white border-b border-border/60">
                    <span className="text-xs font-mono text-ink-muted w-5 select-none">2</span>
                    <input type="checkbox" checked={!!form.bill_show_tax_id} onChange={(e) => setForm({ ...form, bill_show_tax_id: e.target.checked })} />
                    <span className="text-xs font-semibold text-ink-muted w-32 shrink-0">MST</span>
                    <input
                      className={`flex-1 border border-border/60 rounded-lg px-3 py-1.5 text-sm focus:border-bamboo focus:outline-none ${!form.bill_show_tax_id ? "opacity-40" : ""}`}
                      value={form.tax_id || ""}
                      onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
                      placeholder="Mã số thuế..."
                      disabled={!form.bill_show_tax_id}
                    />
                  </div>

                  {/* Dòng 3: Địa chỉ */}
                  <div className="flex items-center gap-3 px-3 py-2.5 bg-white border-b border-border/60">
                    <span className="text-xs font-mono text-ink-muted w-5 select-none">3</span>
                    <input type="checkbox" checked={!!form.bill_show_address} onChange={(e) => setForm({ ...form, bill_show_address: e.target.checked })} />
                    <span className="text-xs font-semibold text-ink-muted w-32 shrink-0">Địa chỉ (Đ/C)</span>
                    <input
                      className={`flex-1 border border-border/60 rounded-lg px-3 py-1.5 text-sm focus:border-bamboo focus:outline-none ${!form.bill_show_address ? "opacity-40" : ""}`}
                      value={form.address || ""}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      placeholder="Địa chỉ cơ sở..."
                      disabled={!form.bill_show_address}
                    />
                  </div>

                  {/* Dòng 4: Email */}
                  <div className="flex items-center gap-3 px-3 py-2.5 bg-white border-b border-border/60">
                    <span className="text-xs font-mono text-ink-muted w-5 select-none">4</span>
                    <input type="checkbox" checked={!!form.bill_show_email} onChange={(e) => setForm({ ...form, bill_show_email: e.target.checked })} />
                    <span className="text-xs font-semibold text-ink-muted w-32 shrink-0">Email</span>
                    <input
                      className={`flex-1 border border-border/60 rounded-lg px-3 py-1.5 text-sm focus:border-bamboo focus:outline-none ${!form.bill_show_email ? "opacity-40" : ""}`}
                      value={form.email || ""}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="Email cơ sở..."
                      disabled={!form.bill_show_email}
                    />
                  </div>

                  {/* Dòng 5: Số điện thoại */}
                  <div className="flex items-center gap-3 px-3 py-2.5 bg-white border-b border-border/60">
                    <span className="text-xs font-mono text-ink-muted w-5 select-none">5</span>
                    <input type="checkbox" checked={!!form.bill_show_phone} onChange={(e) => setForm({ ...form, bill_show_phone: e.target.checked })} />
                    <span className="text-xs font-semibold text-ink-muted w-32 shrink-0">Số điện thoại</span>
                    <input
                      className={`flex-1 border border-border/60 rounded-lg px-3 py-1.5 text-sm focus:border-bamboo focus:outline-none ${!form.bill_show_phone ? "opacity-40" : ""}`}
                      value={form.phone || ""}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="Số điện thoại..."
                      disabled={!form.bill_show_phone}
                    />
                  </div>

                  {/* Dòng 6: Tiêu đề phiếu */}
                  <div className="flex items-center gap-3 px-3 py-2.5 bg-cream/30 border-b border-border/60">
                    <span className="text-xs font-mono text-ink-muted w-5 select-none">6</span>
                    <input type="checkbox" checked={true} disabled className="opacity-40" />
                    <span className="text-xs font-semibold text-ink-muted w-32 shrink-0">Tiêu đề phiếu</span>
                    <input
                      className="flex-1 border border-bamboo/40 rounded-lg px-3 py-1.5 text-sm focus:border-bamboo focus:outline-none bg-white font-semibold"
                      value={form.pxk_title || "Phiếu Xuất Kho Bán Hàng"}
                      onChange={(e) => setForm({ ...form, pxk_title: e.target.value })}
                      placeholder="Phiếu Xuất Kho Bán Hàng"
                    />
                  </div>

                  {/* Dòng 7: Người lập phiếu */}
                  <div className="flex items-center gap-3 px-3 py-2.5 bg-white border-b border-border/60">
                    <span className="text-xs font-mono text-ink-muted w-5 select-none">7</span>
                    <input type="checkbox" checked={true} disabled className="opacity-40" />
                    <span className="text-xs font-semibold text-ink-muted w-32 shrink-0">Người lập phiếu</span>
                    <input
                      className="flex-1 border border-border/60 rounded-lg px-3 py-1.5 text-sm focus:border-bamboo focus:outline-none"
                      value={form.pxk_creator || ""}
                      onChange={(e) => setForm({ ...form, pxk_creator: e.target.value })}
                      placeholder="Tên người lập phiếu (để trống nếu không cần)..."
                    />
                  </div>

                  {/* Dòng 7b: Thủ kho */}
                  <div className="flex items-center gap-3 px-3 py-2.5 bg-white border-b border-border/60">
                    <span className="text-xs font-mono text-ink-muted w-5 select-none"></span>
                    <input type="checkbox" checked={true} disabled className="opacity-40" />
                    <span className="text-xs font-semibold text-ink-muted w-32 shrink-0">Thủ kho</span>
                    <input
                      className="flex-1 border border-border/60 rounded-lg px-3 py-1.5 text-sm focus:border-bamboo focus:outline-none"
                      value={form.pxk_warehouse_keeper || ""}
                      onChange={(e) => setForm({ ...form, pxk_warehouse_keeper: e.target.value })}
                      placeholder="Tên thủ kho (để trống nếu không cần)..."
                    />
                  </div>

                  {/* Dòng 8: Đơn vị tính */}
                  <div className="flex items-center gap-3 px-3 py-2.5 bg-white border-b border-border/60">
                    <span className="text-xs font-mono text-ink-muted w-5 select-none">8</span>
                    <input type="checkbox" checked={true} disabled className="opacity-40" />
                    <span className="text-xs font-semibold text-ink-muted w-32 shrink-0">Đơn vị tính</span>
                    <input
                      className="flex-1 border border-border/60 rounded-lg px-3 py-1.5 text-sm focus:border-bamboo focus:outline-none"
                      value={form.pxk_default_unit || "Cái"}
                      onChange={(e) => setForm({ ...form, pxk_default_unit: e.target.value })}
                      placeholder="Cái, Hộp, Túi..."
                    />
                  </div>

                  {/* Số liên in */}
                  <div className="flex items-center gap-3 px-3 py-2.5 bg-white border-b border-border/60">
                    <span className="text-xs font-mono text-ink-muted w-5 select-none"></span>
                    <input type="checkbox" checked={true} disabled className="opacity-40" />
                    <span className="text-xs font-semibold text-ink-muted w-32 shrink-0">Số liên in bill</span>
                    <div className="flex items-center gap-3 flex-wrap">
                      {[1, 2].map((n) => (
                        <label key={n} className={`flex items-center gap-2 text-xs cursor-pointer px-3 py-1.5 rounded-lg border transition-colors ${(form.bill_print_copies || 1) === n ? "border-bamboo bg-bamboo/5 text-bamboo font-semibold" : "border-border hover:border-bamboo/40"}`}>
                          <input type="radio" name="bill_print_copies" value={n}
                            checked={(form.bill_print_copies || 1) === n}
                            onChange={() => setForm({ ...form, bill_print_copies: n })}
                            className="accent-bamboo" />
                          {n === 1 ? "1 liên" : "2 liên (bản gốc + bản khách)"}
                        </label>
                      ))}
                      {(form.bill_print_copies || 1) === 2 && (
                        <div className="flex items-center gap-2 mt-1 w-full">
                          <span className="text-xs text-ink-muted w-32 shrink-0">Nhãn liên 2:</span>
                          <input
                            value={form.bill_copy2_label || "LIÊN KHÁCH"}
                            onChange={(e) => setForm({ ...form, bill_copy2_label: e.target.value })}
                            className="border border-border/60 rounded px-2 py-1 text-xs focus:border-bamboo focus:outline-none w-40"
                            placeholder="VD: LIÊN KHÁCH"
                          />
                          <span className="text-xs text-ink-muted">— hiển thị trên đầu liên 2</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Dòng 9: Ô ký tên */}
                  <div className="flex items-start gap-3 px-3 py-2.5 bg-white border-b border-border/60">
                    <span className="text-xs font-mono text-ink-muted w-5 pt-1 select-none">9</span>
                    <input type="checkbox" checked={true} disabled className="opacity-40 mt-1" />
                    <span className="text-xs font-semibold text-ink-muted w-32 shrink-0 pt-1">Ô ký tên</span>
                    <div className="flex-1 grid grid-cols-2 gap-2">
                      {[
                        { key: "pxk_sig_buyer", label: "Người mua hàng" },
                        { key: "pxk_sig_warehouse", label: "Thủ kho" },
                        { key: "pxk_sig_shipper", label: "Người giao hàng" },
                        { key: "pxk_sig_creator", label: "Người lập phiếu" },
                        { key: "pxk_show_seller", label: "NV Bán hàng" },
                      ].map((s) => (
                        <label key={s.key} className="flex items-center gap-2 text-xs cursor-pointer p-1.5 rounded border border-border hover:bg-cream/40">
                          <input type="checkbox" checked={form[s.key] === true || form[s.key] === undefined} onChange={(e) => setForm({ ...form, [s.key]: e.target.checked })} />
                          {s.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Dòng 10: Cột mã hàng */}
                  <div className="flex items-center gap-3 px-3 py-2.5 bg-white border-b border-border/60">
                    <span className="text-xs font-mono text-ink-muted w-5 select-none">10</span>
                    <input type="checkbox" checked={form.pxk_show_sku === true || form.pxk_show_sku === undefined} onChange={(e) => setForm({ ...form, pxk_show_sku: e.target.checked })} />
                    <span className="text-xs font-semibold text-ink-muted w-32 shrink-0">Cột Mã hàng</span>
                    <span className="text-xs text-ink-muted">Hiện cột Mã hàng trong bảng hàng hóa</span>
                  </div>

                  {/* Dòng 10b: Cộng (1) */}
                  <div className="flex items-center gap-3 px-3 py-2.5 bg-white border-b border-border/60">
                    <span className="text-xs font-mono text-ink-muted w-5 select-none"></span>
                    <input type="checkbox" checked={form.bill_show_subtotal !== false} onChange={(e) => setForm({ ...form, bill_show_subtotal: e.target.checked })} />
                    <span className="text-xs font-semibold text-ink-muted w-32 shrink-0">Dòng Cộng (1)</span>
                    <span className="text-xs text-ink-muted">Hiện dòng "Cộng (1)" tổng tiền hàng trước chiết khấu</span>
                  </div>

                  {/* Dòng 11: Chiết khấu */}
                  <div className="flex items-center gap-3 px-3 py-2.5 bg-white border-b border-border/60">
                    <span className="text-xs font-mono text-ink-muted w-5 select-none">11</span>
                    <input type="checkbox" checked={form.bill_show_discount !== false} onChange={(e) => setForm({ ...form, bill_show_discount: e.target.checked })} />
                    <span className="text-xs font-semibold text-ink-muted w-32 shrink-0">Chiết khấu</span>
                    <span className="text-xs text-ink-muted">Dòng chiết khấu / giảm giá trên phiếu</span>
                  </div>

                  {/* Dòng 11b: Sau chiết khấu */}
                  <div className={`flex items-center gap-3 px-3 py-2.5 border-b border-border/60 ${form.bill_show_discount !== false ? "bg-white" : "bg-gray-50 opacity-50 pointer-events-none"}`}>
                    <span className="text-xs font-mono text-ink-muted w-5 select-none"></span>
                    <input type="checkbox" checked={form.bill_show_after_discount !== false} onChange={(e) => setForm({ ...form, bill_show_after_discount: e.target.checked })} disabled={form.bill_show_discount === false} />
                    <span className="text-xs font-semibold text-ink-muted w-32 shrink-0">Sau chiết khấu</span>
                    <span className="text-xs text-ink-muted">Dòng tổng sau khi trừ chiết khấu (= Cộng - Chiết khấu)</span>
                  </div>

                  {/* Dòng 12: QR Ngân hàng */}
                  <div className="flex items-center gap-3 px-3 py-2.5 bg-white border-b border-border/60">
                    <span className="text-xs font-mono text-ink-muted w-5 select-none">12</span>
                    <input type="checkbox" checked={!!form.bill_show_bank_qr} onChange={(e) => setForm({ ...form, bill_show_bank_qr: e.target.checked })} />
                    <span className="text-xs font-semibold text-ink-muted w-32 shrink-0">QR Ngân hàng</span>
                    <span className={`flex-1 text-xs px-3 py-1.5 rounded-lg border border-dashed ${form.bill_show_bank_qr && form.bank_account ? "text-green-700 border-green-300 bg-green-50" : "text-ink-muted border-border/40"}`}>
                      {form.bank_name && form.bank_account
                        ? `${form.bank_name} · ${form.bank_account}${form.bank_account_holder ? ` · ${form.bank_account_holder}` : ""}`
                        : "Chưa có STK — xem tab QR Ngân hàng"}
                    </span>
                  </div>

                  {/* Dòng 13: Khổ giấy */}
                  <div className="flex items-center gap-3 px-3 py-2.5 bg-white">
                    <span className="text-xs font-mono text-ink-muted w-5 select-none">13</span>
                    <input type="checkbox" checked={true} disabled className="opacity-40" />
                    <span className="text-xs font-semibold text-ink-muted w-32 shrink-0">Khổ giấy</span>
                    <div className="flex gap-2 flex-1">
                      {[
                        { value: "a4", label: "A4 (210×297mm)" },
                        { value: "a5", label: "A5 (148×210mm)" },
                        { value: "thermal", label: "Nhiệt 80mm" },
                      ].map((opt) => (
                        <label key={opt.value} className={`flex items-center gap-1.5 text-xs cursor-pointer px-3 py-1.5 rounded-lg border transition-colors ${(form.pxk_paper_size || "a4") === opt.value ? "border-bamboo bg-bamboo/10 text-bamboo font-semibold" : "border-border text-ink-muted hover:bg-cream/40"}`}>
                          <input type="radio" name="pxk_paper_size" value={opt.value} checked={(form.pxk_paper_size || "a4") === opt.value} onChange={() => setForm({ ...form, pxk_paper_size: opt.value })} className="hidden" />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-xs text-ink-muted">💡 Tick ô vuông để bật/tắt từng dòng. Sửa trực tiếp nội dung bên phải.</p>
              </div>
            )}

            {/* ── TAB: QR NGÂN HÀNG ── */}
            {billTab === "qr" && (
              <div className="p-5">
                {form.bank_name && form.bank_account ? (
                  <div className="space-y-4">
                    <Label className="text-sm font-semibold block">Xem trước QR thanh toán</Label>
                    <div className="flex items-start gap-4 p-4 bg-cream/40 rounded-xl border border-border">
                      {(() => {
                        const map = {
                          "vietcombank": "VCB", "vcb": "VCB", "techcombank": "TCB", "tcb": "TCB",
                          "bidv": "BIDV", "vietinbank": "ICB", "icb": "ICB", "agribank": "VBA",
                          "mbbank": "MB", "mb": "MB", "acb": "ACB", "tpbank": "TPB", "tpb": "TPB",
                          "vpbank": "VPB", "vpb": "VPB", "sacombank": "STB", "stb": "STB",
                          "hdbank": "HDB", "msb": "MSB", "shb": "SHB", "vib": "VIB",
                          "lpbank": "LPB", "ocb": "OCB", "eximbank": "EIB",
                        };
                        const low = (form.bank_name || "").toLowerCase().replace(/\s+/g, "");
                        const code = map[low] || form.bank_name.replace(/\s+/g, "").toUpperCase();
                        const qrUrl = `https://img.vietqr.io/image/${code}-${form.bank_account}-compact2.png?amount=295000&addInfo=Demo+QR${form.bank_account_holder ? `&accountName=${encodeURIComponent(form.bank_account_holder)}` : ""}`;
                        return (
                          <img src={qrUrl} alt="QR Preview" crossOrigin="anonymous" className="rounded-lg border border-border shadow-sm" style={{ width: 140, height: 140, objectFit: "contain", background: "#fff" }} onError={(e) => { e.target.style.display = "none"; }} />
                        );
                      })()}
                      <div className="flex-1 text-sm space-y-1">
                        <div className="font-semibold text-ink">{form.bank_name}</div>
                        <div className="font-mono text-bamboo text-lg tracking-wider">{form.bank_account}</div>
                        {form.bank_account_holder && <div className="text-ink-muted uppercase tracking-wide text-xs">{form.bank_account_holder}</div>}
                        <div className="text-xs text-ink-muted pt-1">
                          QR sẽ in trên phiếu khi bật dòng 12 (tab Nội dung).<br />
                          Số tiền tự động điền theo đơn hàng khi in thực tế.
                        </div>
                        {!form.bill_show_bank_qr && (
                          <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 mt-1">
                            ⚠️ QR đang tắt — vào tab Nội dung, tick dòng 12 để bật.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-10 text-ink-muted">
                    <div className="text-3xl mb-2">🏦</div>
                    <p className="text-sm">Chưa có thông tin ngân hàng.</p>
                    <p className="text-xs mt-1">Vào mục <b>Ngân Hàng</b> bên trên để thêm số tài khoản.</p>
                  </div>
                )}
              </div>
            )}

            {/* ── TAB: XEM TRƯỚC ── */}
            {billTab === "preview" && (
              <div className="p-5 space-y-3">
                <Label className="flex items-center gap-2"><Eye size={14} /> Xem trước Phiếu Xuất Kho (dữ liệu mẫu)</Label>
                <div className="border border-border rounded-xl overflow-hidden bg-gray-50">
                  <div className="overflow-auto max-h-[700px] p-4 flex justify-center">
                    <div style={{
                      transform: form.pxk_paper_size === "thermal" ? "scale(0.9)" : form.pxk_paper_size === "a5" ? "scale(0.82)" : "scale(0.72)",
                      transformOrigin: "top center"
                    }}>
                      <PhieuXuatKho shop={form} order={DEMO_ORDER} />
                    </div>
                  </div>
                </div>
                <p className="text-xs text-ink-muted text-center">* Xem trước với dữ liệu mẫu. Nhớ bấm <b>Lưu cài đặt</b> để áp dụng thay đổi.</p>
              </div>
            )}
            </div>{/* end scrollable */}

            {/* Modal footer */}
            <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-border bg-cream/30 shrink-0">
              <Button type="button" variant="outline" onClick={() => setBillOpen(false)}>Đóng</Button>
            </div>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <button
            type="button"
            className="flex items-center justify-between w-full text-left"
            onClick={() => setBgOpen(v => !v)}
          >
            <CardTitle className="flex items-center gap-2">
              🎨 Giao diện nền
            </CardTitle>
            {bgOpen ? <ChevronUp size={18} className="text-ink-muted" /> : <ChevronDown size={18} className="text-ink-muted" />}
          </button>
          {!bgOpen && (
            <p className="text-sm text-ink-muted mt-1">
              {BG_PRESETS.find(p => p.id === bgConfig.type)?.label || "Tùy chỉnh"} · Bấm để thay đổi nền trang
            </p>
          )}
        </CardHeader>
        {bgOpen && (
          <CardContent className="space-y-5">
            {/* Preset chọn nhanh */}
            <div>
              <Label>Chọn nền có sẵn</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {BG_PRESETS.map((p) => {
                  const active = bgConfig.type === p.id;
                  let previewStyle = {};
                  if (p.id === "default") previewStyle = { background: "#fafafa", backgroundImage: "radial-gradient(circle at 1px 1px,rgba(45,74,34,0.06) 1px,transparent 0)", backgroundSize: "16px 16px" };
                  else if (p.value === "dot") previewStyle = { background: "#fafafa", backgroundImage: "radial-gradient(circle,#2D4A2222 1px,transparent 0)", backgroundSize: "12px 12px" };
                  else if (p.value === "grid") previewStyle = { background: "#fafafa", backgroundImage: "linear-gradient(rgba(45,74,34,0.08) 1px,transparent 1px),linear-gradient(90deg,rgba(45,74,34,0.08) 1px,transparent 1px)", backgroundSize: "16px 16px" };
                  else if (p.value === "wave") previewStyle = { background: "linear-gradient(135deg,#f4f1ea 25%,#e8f5e9 50%,#f4f1ea 75%)" };
                  else previewStyle = { background: p.value };
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => applyBg({ ...bgConfig, type: p.id, value: p.value || "" })}
                      className={`relative h-16 rounded-lg border-2 overflow-hidden transition-all ${active ? "border-bamboo shadow-md" : "border-border hover:border-bamboo/50"}`}
                      style={previewStyle}
                    >
                      {active && <div className="absolute inset-0 flex items-center justify-center"><div className="w-5 h-5 rounded-full bg-bamboo/90 flex items-center justify-center text-white text-xs">✓</div></div>}
                      <span className="absolute bottom-1 left-0 right-0 text-center text-[10px] font-medium text-ink/70 bg-white/60 py-0.5">{p.label}</span>
                    </button>
                  );
                })}
                {/* Ô upload ảnh */}
                <button
                  key="custom_img"
                  type="button"
                  onClick={() => bgImgRef.current?.click()}
                  className={`relative h-16 rounded-lg border-2 overflow-hidden transition-all flex flex-col items-center justify-center gap-1 ${bgConfig.type === "custom_img" ? "border-bamboo shadow-md" : "border-dashed border-border hover:border-bamboo/50"}`}
                  style={bgConfig.type === "custom_img" && bgConfig.imgData ? { backgroundImage: `url(${bgConfig.imgData})`, backgroundSize: "cover", backgroundPosition: "center" } : { background: "#fafafa" }}
                >
                  {bgConfig.type !== "custom_img" && <><Upload size={16} className="text-ink-muted" /><span className="text-[10px] text-ink-muted">Ảnh riêng</span></>}
                  {bgConfig.type === "custom_img" && <div className="absolute inset-0 flex items-center justify-center bg-black/20"><div className="w-5 h-5 rounded-full bg-bamboo/90 flex items-center justify-center text-white text-xs">✓</div></div>}
                  <span className="absolute bottom-1 left-0 right-0 text-center text-[10px] font-medium text-ink/70 bg-white/60 py-0.5">Ảnh riêng</span>
                </button>
                <input ref={bgImgRef} type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 2 * 1024 * 1024) { toast({ title: "Ảnh quá lớn, tối đa 2MB", variant: "error" }); return; }
                  const b64 = await fileToBase64(file);
                  applyBg({ ...bgConfig, type: "custom_img", imgData: b64 });
                  if (bgImgRef.current) bgImgRef.current.value = "";
                }} />
              </div>
            </div>

            {/* Độ mờ */}
            {bgConfig.type !== "default" && (
              <div>
                <Label>Độ mờ nền ({Math.round((bgConfig.opacity ?? 0.08) * 100)}%)</Label>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs text-ink-muted">Mờ</span>
                  <input
                    type="range" min={0.02} max={0.5} step={0.01}
                    value={bgConfig.opacity ?? 0.08}
                    onChange={(e) => applyBg({ ...bgConfig, opacity: Number(e.target.value) })}
                    className="flex-1 accent-bamboo"
                  />
                  <span className="text-xs text-ink-muted">Rõ</span>
                </div>
              </div>
            )}

            {/* Nút reset */}
            {bgConfig.type !== "default" && (
              <button
                type="button"
                onClick={() => applyBg({ type: "default", value: "", opacity: 0.08 })}
                className="text-xs text-red-500 hover:underline"
              >
                ↩ Khôi phục mặc định
              </button>
            )}
          </CardContent>
        )}
      </Card>

      <Card>
        <CardHeader><CardTitle>{t("common.language")}</CardTitle></CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Button type="button" variant={lang === "vi" ? "primary" : "outline"} onClick={() => lang !== "vi" && toggleLang()}>Tiếng Việt</Button>
            <Button type="button" variant={lang === "en" ? "primary" : "outline"} onClick={() => lang !== "en" && toggleLang()}>English</Button>
          </div>
        </CardContent>
      </Card>

      {user?.role === "admin" && (
        <Card className="border-amber-200">
          <CardHeader>
            <button
              type="button"
              className="flex items-center justify-between w-full text-left"
              onClick={() => setSecOpen(v => !v)}
            >
              <div className="flex items-center gap-2">
                <ShieldCheck size={18} className="text-amber-700" />
                <CardTitle className="text-amber-800">Bảo mật cấp 2</CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  {secStatus.has_pin2 && <Badge variant="delivered">PIN2</Badge>}
                  {secStatus.has_delete_pins && <Badge variant="delivered">Xóa</Badge>}
                </div>
                {secOpen ? <ChevronUp size={18} className="text-ink-muted" /> : <ChevronDown size={18} className="text-ink-muted" />}
              </div>
            </button>
            {!secOpen && (
              <p className="text-sm text-ink-muted mt-1">
                {secStatus.has_pin2 || secStatus.has_delete_pins
                  ? `Đã thiết lập: ${[secStatus.has_pin2 && "PIN2", secStatus.has_delete_pins && "Mật khẩu xóa"].filter(Boolean).join(", ")}`
                  : "Bấm để thiết lập mật khẩu cấp 2 và mật khẩu xóa"}
              </p>
            )}
          </CardHeader>
          {secOpen && (
          <CardContent className="space-y-6">
            <div className="text-xs text-ink-secondary">
              Mật khẩu cấp 2 sẽ được yêu cầu khi <strong>sửa trạng thái</strong> hoặc <strong>xóa đơn hàng</strong>. Mọi nhân viên (kể cả admin) đều phải nhập.
            </div>

            {/* PIN2 */}
            <div className="border border-border rounded p-3 space-y-3" data-testid="settings-pin2-card">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <KeyRound size={16} className="text-bamboo" />
                  <div className="font-medium text-sm">Mật khẩu cấp 2 (PIN2)</div>
                </div>
                <Badge variant={secStatus.has_pin2 ? "delivered" : "cancelled"} data-testid="pin2-status">
                  {secStatus.has_pin2 ? "Đã thiết lập" : "Chưa có"}
                </Badge>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Mật khẩu tài khoản (xác thực)</Label>
                  <div className="relative">
                    <Input
                      type={showPw.pin2_acc ? "text" : "password"}
                      value={pin2Form.account_password}
                      onChange={(e) => setPin2Form({ ...pin2Form, account_password: e.target.value })}
                      className="pr-9"
                      data-testid="pin2-account-pass"
                    />
                    <button type="button" onClick={() => setShowPw(p => ({ ...p, pin2_acc: !p.pin2_acc }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink transition-colors" tabIndex={-1}>
                      {showPw.pin2_acc ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label>Mật khẩu cấp 2 mới (≥ 4 ký tự)</Label>
                  <div className="relative">
                    <Input
                      type={showPw.pin2_new ? "text" : "password"}
                      value={pin2Form.new_pin}
                      onChange={(e) => setPin2Form({ ...pin2Form, new_pin: e.target.value })}
                      className="pr-9"
                      data-testid="pin2-new-pin"
                    />
                    <button type="button" onClick={() => setShowPw(p => ({ ...p, pin2_new: !p.pin2_new }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink transition-colors" tabIndex={-1}>
                      {showPw.pin2_new ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="button" size="sm" onClick={savePin2} data-testid="pin2-save">
                  {secStatus.has_pin2 ? "Cập nhật PIN2" : "Tạo PIN2"}
                </Button>
                {secStatus.has_pin2 && (
                  <Button type="button" size="sm" variant="outline" className="text-red-700 border-red-300" onClick={clearPin2} data-testid="pin2-clear">
                    Tắt PIN2
                  </Button>
                )}
              </div>
            </div>

            {/* Delete-all PINs */}
            <div className="border border-border rounded p-3 space-y-3" data-testid="settings-delete-pins-card">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Shield size={16} className="text-red-700" />
                  <div className="font-medium text-sm">2 mật khẩu xóa toàn bộ đơn hàng</div>
                </div>
                <Badge variant={secStatus.has_delete_pins ? "delivered" : "cancelled"} data-testid="delete-pins-status">
                  {secStatus.has_delete_pins ? "Đã thiết lập" : "Chưa có"}
                </Badge>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label>Mật khẩu tài khoản</Label>
                  <div className="relative">
                    <Input
                      type={showPw.del_acc ? "text" : "password"}
                      value={delPinsForm.account_password}
                      onChange={(e) => setDelPinsForm({ ...delPinsForm, account_password: e.target.value })}
                      className="pr-9"
                      data-testid="del-pins-account-pass"
                    />
                    <button type="button" onClick={() => setShowPw(p => ({ ...p, del_acc: !p.del_acc }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink transition-colors" tabIndex={-1}>
                      {showPw.del_acc ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label>Mật khẩu xóa A</Label>
                  <div className="relative">
                    <Input
                      type={showPw.del_a ? "text" : "password"}
                      value={delPinsForm.pin_a}
                      onChange={(e) => setDelPinsForm({ ...delPinsForm, pin_a: e.target.value })}
                      className="pr-9"
                      data-testid="del-pins-a"
                    />
                    <button type="button" onClick={() => setShowPw(p => ({ ...p, del_a: !p.del_a }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink transition-colors" tabIndex={-1}>
                      {showPw.del_a ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label>Mật khẩu xóa B</Label>
                  <div className="relative">
                    <Input
                      type={showPw.del_b ? "text" : "password"}
                      value={delPinsForm.pin_b}
                      onChange={(e) => setDelPinsForm({ ...delPinsForm, pin_b: e.target.value })}
                      className="pr-9"
                      data-testid="del-pins-b"
                    />
                    <button type="button" onClick={() => setShowPw(p => ({ ...p, del_b: !p.del_b }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink transition-colors" tabIndex={-1}>
                      {showPw.del_b ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
              </div>
              <Button type="button" size="sm" className="bg-red-600 hover:bg-red-700" onClick={saveDeletePins} data-testid="del-pins-save">
                {secStatus.has_delete_pins ? "Cập nhật 2 mật khẩu xóa" : "Tạo 2 mật khẩu xóa"}
              </Button>
            </div>
          </CardContent>
          )}
        </Card>
      )}

      {user?.role === "admin" && (
        <Card>
          <CardHeader>
            <button
              type="button"
              className="flex items-center justify-between w-full text-left"
              onClick={() => setBackupOpen(v => !v)}
            >
              <div className="flex items-center gap-2">
                <Upload size={18} className="text-bamboo" />
                <CardTitle>Sao lưu &amp; Xuất nhập dữ liệu</CardTitle>
              </div>
              {backupOpen ? <ChevronUp size={18} className="text-ink-muted" /> : <ChevronDown size={18} className="text-ink-muted" />}
            </button>
            {!backupOpen && (
              <p className="text-sm text-ink-muted mt-1">Bấm để xuất Excel, backup .zip hoặc đồng bộ dữ liệu</p>
            )}
          </CardHeader>
          {backupOpen && (
          <CardContent className="space-y-3">
            <div className="text-xs text-ink-secondary">
              Xuất toàn bộ dữ liệu (Khách hàng, Đơn hàng, Sản phẩm, Lịch sử thanh toán) thành 1 file Excel hoặc 1 file backup .zip. Có thể đồng bộ ngược lên hệ thống bằng file Excel đã tải.
              <br />
              <span className="text-amber-700">Lưu ý:</span> với sheet "orders", chỉ các đơn hàng <strong>đã có sẵn</strong> trong hệ thống mới được cập nhật (trạng thái, thanh toán...); các dòng có mã đơn mới sẽ bị bỏ qua vì file Excel không chứa chi tiết sản phẩm của đơn.
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button type="button" variant="outline" onClick={exportAllExcel} data-testid="settings-export-all-excel">
                <Upload size={14} className="rotate-180" /> Xuất Excel (đa sheet, tiếng Việt)
              </Button>
              <Button type="button" variant="outline" onClick={backupZip} data-testid="settings-backup-zip">
                <Upload size={14} className="rotate-180" /> Backup .zip (full JSON)
              </Button>
              <input
                ref={importAllRef}
                type="file"
                accept=".xlsx"
                onChange={onImportAll}
                className="hidden"
                data-testid="settings-import-all-input"
              />
              <Button type="button" variant="outline" onClick={() => importAllRef.current?.click()} data-testid="settings-import-all-btn">
                <Upload size={14} /> Đồng bộ ngược từ Excel
              </Button>
            </div>
          </CardContent>
          )}
        </Card>
      )}

      {user?.role === "admin" && (
        <Card className="border-red-200 bg-red-50/30">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-red-700" />
              <CardTitle className="text-red-800">Khu vực nguy hiểm</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <Button type="button" variant="outline" className="text-red-700 border-red-300 hover:bg-red-100" onClick={onResetDemo} data-testid="settings-reset-demo">
              {t("settings.resetDemoTitle")}
            </Button>
            <p className="text-xs text-red-700/80 mt-2">{t("settings.resetDemoConfirm")}</p>
          </CardContent>
        </Card>
      )}
    </form>
  );
}